/**
 * What Reppr would add to the address book on the agent's Mac, and the cards
 * that need a second look. address_book_changes is owner-readable and
 * server-written, so decisions go through /api/capture/address-book.
 */

import { pb } from '../lib/pocketbase'

export type BookChangeKind = 'create_card' | 'add_phone' | 'add_email' | 'add_to_group' | 'duplicate_cards' | 'unusable_number'

export type BookChangeItem = {
  id: string
  kind: BookChangeKind
  status: 'proposed' | 'approved' | 'applied' | 'failed'
  value: string
  display_value: string
  card_name: string | null
  detail: Record<string, any>
  error: string | null
  applied_at: string | null
  /** Null when the person is no longer in Reppr with a usable name and number. */
  person: { name: string; role: 'self' | 'parent'; group: string; entity_type: 'client' | 'prospect' | 'contact'; entity_id: string } | null
}

export type BookChanges = {
  proposals: BookChangeItem[]
  notices: BookChangeItem[]
  /** Approved, for the Mac to apply on its next run. */
  waiting: BookChangeItem[]
  /** Applied or failed in the last 30 days. */
  recent: BookChangeItem[]
}

async function call(body?: Record<string, unknown>) {
  const res = await fetch('/api/capture/address-book', {
    method: body ? 'POST' : 'GET',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  })
  const text = await res.text()
  let data: Record<string, any>
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error(
      `Address book changes returned ${res.status} but not JSON. If you are running the dev server on its own, this endpoint only exists under \`vercel dev\`.`,
    )
  }
  if (!res.ok) throw new Error(data.error || `Could not reach address book changes (${res.status}).`)
  return data
}

export async function listBookChanges(): Promise<BookChanges> {
  const data = await call()
  for (const k of ['proposals', 'notices', 'waiting', 'recent']) {
    if (!Array.isArray(data[k])) throw new Error('Address book changes did not return every list.')
  }
  return data as BookChanges
}

export async function decideBookChanges(action: 'approve' | 'skip' | 'dismiss' | 'retry', ids: string[]) {
  return call({ action, ids }) as Promise<Record<string, any>>
}
